<template>
  <div
    :class="[
      'base-chip',
      `base-chip--${theme}`,
      `base-chip--size-${size}`,
      { 'base-chip--removable': removable },
      { 'base-chip--loading': isLoading },
    ]"
  >
    <div v-if="hasIcon" class="base-chip__icon">
      <slot name="icon" />
    </div>

    <slot />

    <ButtonClose
      v-if="removable"
      size="tiny"
      :title="removeButtonTitle"
      class="base-chip__remove-button"
      @click="remove"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import ButtonClose from '../ButtonClose.vue'

export interface BaseChipProps {
  theme?: 'gray' | 'blue' | 'red-alert' | 'new-leaf' | 'portal-blue'
  value?: string
  size?: 'tiny' | 'small'
  removable?: boolean
  removeButtonTitle?: string
  isLoading?: boolean
}

// Define props with defaults
const props = withDefaults(defineProps<BaseChipProps>(), {
  theme: 'gray',
  value: '',
  size: 'small',
  removable: false,
  removeButtonTitle: 'Remove',
  isLoading: false,
})

const slots = useSlots()
const hasIcon = computed(() => Boolean(slots.icon))

// Define emits
const emit = defineEmits<{
  (e: 'onRemove', value: string): void
}>()

// Component methods
const remove = () => {
  emit('onRemove', props.value)
}
</script>

<style lang="scss" scoped>
.base-chip {
  @include micro-header;
  text-align: center;
  font-size: 11px;
  letter-spacing: 1px;
  line-height: 14px;

  display: inline-flex;
  align-items: center;
  border-radius: $border-radius-xl;
  border: 1px solid $color-border;
  background: $color-canvas;
  color: $color-text;
  white-space: nowrap;

  &__icon {
    display: flex;
    align-items: center;
    margin-right: $spacing-4xs;
    width: $spacing-3xs;
    height: $spacing-3xs;
  }

  &--size-tiny {
    padding: $spacing-5xs $spacing-4xs;
  }

  &--size-small {
    padding: $spacing-4xs $spacing-3xs;
  }

  &--removable {
    padding: $spacing-5xs $spacing-5xs $spacing-5xs $spacing-3xs;
  }

  // Matches the outline blue icon buttons
  &--blue {
    color: $color-blue-45;
  }

  &--red-alert {
    background: $color-red-05;
    color: $color-red-60;
    border-color: transparent;
  }

  &--new-leaf {
    background: $color-green-05;
    color: $color-green-60;
    border-color: transparent;
  }

  &--portal-blue {
    background: $color-blue-10;
    color: $color-blue-60;
    border-color: transparent;
  }

  &--loading {
    opacity: 0.75;
    pointer-events: none;
  }
}
</style>
